import { materialTypeFormat } from '@/common/material-type-formatting';
import { MobileContainer } from '@/components/workflow/mobile-container';
import { MobileHeader } from '@/components/workflow/mobile-header';
import { Badge, Card, Flex, Stack, Text, Title } from '@mantine/core';
import { IconCheck, IconPackage } from '@tabler/icons-react';
import { useBaleProcessedContext } from '../hook';

export const BaleProcessedConfirmationPage = () => {
	const { setPage, scannedBales } = useBaleProcessedContext();

	// Group bales by material type
	const materialCounts = scannedBales.reduce(
		(acc, bale) => {
			const material = bale.materialType;
			acc[material] = (acc[material] || 0) + 1;
			return acc;
		},
		{} as Record<string, number>
	);

	const materials = Object.entries(materialCounts);

	return (
		<MobileContainer
			header={<MobileHeader title="Confirm Processing" onBack={() => setPage('SCAN')} />}
		>
			<Stack gap="lg" p="md" style={{ minHeight: '100%', paddingBottom: 120 }}>
				{/* Header Section */}
				<Flex direction="column" align="center" gap="sm" py="xl">
					<Flex
						align="center"
						justify="center"
						w={80}
						h={80}
						style={{
							backgroundColor: 'var(--mantine-color-green-0)',
							borderRadius: '50%',
							border: '2px solid var(--mantine-color-green-3)',
						}}
					>
						<IconCheck size={40} color="var(--mantine-color-green-3)" />
					</Flex>

					<Text size="xl" fw={600} ta="center" c="dark">
						Ready to Process
					</Text>

					<Badge size="lg" variant="filled" color="green">
						{scannedBales.length} Bale{scannedBales.length !== 1 ? 's' : ''}
					</Badge>
				</Flex>

				{/* Materials Summary */}
				<Card shadow="sm" padding="lg" radius="md">
					<Title order={3} mb="md" size="md" fw={600}>
						Material Breakdown
					</Title>

					{materials.length > 0 ? (
						<Stack gap="sm">
							{materials.map(([materialType, count]) => {
								const material =
									materialTypeFormat[materialType as keyof typeof materialTypeFormat];
								return (
									<Flex
										key={materialType}
										align="center"
										justify="space-between"
										p="sm"
										style={{
											backgroundColor: 'white',
											borderRadius: '8px',
											border: '1px solid var(--mantine-color-gray-1)',
										}}
									>
										<Flex align="center" gap="sm">
											<Text size="lg" style={{ minWidth: '24px' }}>
												{material?.emoji || '📦'}
											</Text>
											<Text size="sm" fw={500}>
												{material?.label || materialType}
											</Text>
										</Flex>
										<Badge size="sm" variant="light" color="blue">
											{count}
										</Badge>
									</Flex>
								);
							})}
						</Stack>
					) : (
						<Flex direction="column" align="center" justify="center" py="xl" gap="sm" c="gray">
							<IconPackage size={32} />
							<Text size="sm">No bales to process</Text>
						</Flex>
					)}
				</Card>

				{/* Information Note */}
				<Card
					padding="md"
					radius="sm"
					style={{
						backgroundColor: 'var(--mantine-color-blue-0)',
						border: '1px solid var(--mantine-color-blue-2)',
					}}
				>
					<Text size="sm" ta="center" lh={1.4}>
						These bales will be marked as processed and moved to your facility's completed
						inventory.
					</Text>
				</Card>
			</Stack>
		</MobileContainer>
	);
};
